import { DatePipe, NgClass } from "@angular/common";
import { Component, ViewChild, ViewContainerRef } from "@angular/core";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { AdminUnauthorizedComponent } from "../admin-unauthorized.component";
import { AuthService } from "@ponder-core/auth/auth.service";
import { RoleAndPermissions } from "@ponder-core/auth/role-permissions";
import { UserPermissionType } from "@apiserver-rest/ponder-auth-client-base";
import { AdminNewsPostEditorComponent } from "./news-post-editor.component";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { Subscription, take } from "rxjs";
import { DataResponse, GetNewsPostStubsResponse, NewsPostStub } from "@apiserver-rest/ponder-data-client-base";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { AuthSession } from "@ponder-core/auth/auth-session";
import { ConfirmationModalComponent } from "@ponder-shared/modals/confirmation-modal.component";

@Component({
    selector: "admin-news",
    imports: [NgClass, DatePipe, LoadingComponent, AdminUnauthorizedComponent, AdminNewsPostEditorComponent],
    templateUrl: "./admin-news.component.html"
})
export class AdminNewsComponent {
    @ViewChild("confirmationModalTemplate", { read: ViewContainerRef }) private confirmationModalVcr!: ViewContainerRef;
    
    public loadingCanView: boolean = true;
    public canView: boolean = false;
    public isDeleting: boolean = false;

    public username?: string;
    public loadingStubs: boolean = true;
    public loadingStubsSuccess: boolean = false;
    public posts: NewsPostStub[] = [];
    public selectedStub?: NewsPostStub;

    public showEditor: boolean = false;

    private editCancelledSub!: Subscription;

    public constructor(private authService: AuthService, private ponderDataClient: PonderDataClient, private events: EventBroker) {
        this.editCancelledSub = this.events.ui.onEditCancelled.subscribe(() => {
            this.selectedStub = undefined;
            this.showEditor = false;
        });
    }

    public ngOnDestroy(): void {
        this.editCancelledSub?.unsubscribe();
    }

    public ngOnInit(): void {
        this.authService.getSessionAsync().then((authSession?: AuthSession) => {
            this.username = authSession?.username;

            if (this.username) {
                this.authService.getRoleAndPermissions().then((roleAndPermissions?: RoleAndPermissions) => {
                    this.canView = roleAndPermissions?.hasPermission(UserPermissionType.PostNews) == true;
                    this.loadingCanView = false;
        
                    if (this.canView) {
                        this.ponderDataClient
                            .newsStubs()
                            .pipe(take(1))
                            .subscribe({
                                next: (response: GetNewsPostStubsResponse) => {
                                    if (response.success) {
                                        this.loadingStubsSuccess = true;
                                        this.posts = response.posts ?? [];
                                    }
                                }
                            })
                            .add(() => { this.loadingStubs = false })
                    }
                });
            }
        });
    }

    public onNewPostClicked(): void {
        this.selectedStub = undefined;
        this.showEditor = true;
    }

    public onEditClicked(post: NewsPostStub): void {
        this.selectedStub = post;
        this.showEditor = true;
    }

    public onDeleteClicked(post: NewsPostStub): void {
        if (this.showEditor || this.isDeleting) {
            return;
        }

        this.isDeleting = true;

        var confirmationModalRef = this.confirmationModalVcr.createComponent(ConfirmationModalComponent);

        confirmationModalRef.instance.title = "Delete News Post";
        confirmationModalRef.instance.body = `Delete news post '${post.title}'?`;
        confirmationModalRef.instance.onCancelCallback = () => { this.isDeleting = false; }
        confirmationModalRef.instance.onAcceptCallback = () => {
            this.ponderDataClient
                .newsPostDelete(post.id)
                .pipe(take(1))
                .subscribe({
                    next: (response: DataResponse) => {
                        const index = this.posts.findIndex(p => {
                            return p.id == post.id;
                        });

                        if (index >= 0) {
                            this.posts.splice(index, 1);
                        }
                    }
                })
                .add(() => this.isDeleting = false);
        };
    }
}